/* global gtag */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Container,
  Paper,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logoImage from "../../assets/new_logo.png";
import { loginAPI } from "../../services/authService";
import { useDispatch } from "react-redux";
import { setAccount, showAppAlert } from "../../store/reducers/appreducer";
import VersionPrompt from "../../components/VersionPrompt";
import {
  getUiPreference,
  setUiPreference,
  UI_V1,
  UI_LEGACY,
} from "../../utils/uiPreference";

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showVersionPrompt, setShowVersionPrompt] = useState(false);
  const [data, setData] = useState({
    email: "",
    password: "",
  });

  // Either answer is remembered, so the prompt is shown once and never again.
  const chooseUi = (preference) => {
    setUiPreference(preference);
    setShowVersionPrompt(false);
    navigate(preference === UI_V1 ? "/v1/jobs" : "/dashboard");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      setLoading(true);
      try {
        const response = await loginAPI(data.email, data.password);
        setLoading(false);
        if (response.error) {
          setErrors({ form: response.errorMessage });
          dispatch(
            showAppAlert({
              show: true,
              alertType: "error",
              alertMessage: response.errorMessage,
            })
          );
        } else {
          dispatch(setAccount({ data: response, userId: response.userId, company_id: response.company_id }));
          dispatch(
            showAppAlert({
              show: true,
              alertType: "success",
              alertMessage: "Signed in successfully!",
            })
          );

          // Already chosen a UI? Honour it. Otherwise ask once — the prompt renders over
          // the login page, so we don't navigate until they've answered.
          const preference = getUiPreference();
          if (preference === UI_V1) {
            navigate("/v1/jobs");
          } else if (preference === UI_LEGACY) {
            navigate("/dashboard");
          } else {
            setShowVersionPrompt(true);
          }
        }
      } catch (err) {
        setLoading(false);
        setErrors({ form: "An error occurred. Please try again." });
        console.error("Error during login:", err);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const validateForm = () => {
    let tempErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email) {
      tempErrors.email = "Email is required";
    } else if (!emailPattern.test(data.email)) {
      tempErrors.email = "Invalid email format";
    }
    tempErrors.password = !data.password ? "Password is required" : "";
    setErrors(tempErrors);
    return Object.values(tempErrors).every((error) => !error);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <VersionPrompt
        open={showVersionPrompt}
        onAccept={() => chooseUi(UI_V1)}
        onDecline={() => chooseUi(UI_LEGACY)}
      />
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 480, width: '100%', mx: 'auto' }}>
          {/* Logo Only */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5, mb: 2 }}>
            <img
              src={logoImage}
              alt="Logo"
              style={{ height: 40, width: "auto", maxWidth: "60%", objectFit: "contain" }}
            />
          </Box>

          {/* Welcome Text */}
          <Typography variant="h5" align="left" sx={{ mb: 3, fontWeight: 700, fontFamily: "'Roboto', sans-serif" }}>
            Sign in
          </Typography>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Box mb={2}>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#262626", fontWeight: 500 }}>
                Email Address
              </Typography>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="email"
                placeholder="Enter your email"
                name="email"
                value={data.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Box mb={1}>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#262626", fontWeight: 500 }}>
                Password
              </Typography>
              <TextField
                variant="outlined"
                required
                fullWidth
                name="password"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                id="password"
                value={data.password}
                onChange={handleChange}
                error={Boolean(errors.password)}
                helperText={errors.password}
                sx={{ mt: 0.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Link
                to="/forgotpwd"
                style={{ textDecoration: "none", color: "#5088FF", fontWeight: 500 }}
              >
                Forgot Password?
              </Link>
            </Box>
            {errors.form && (
              <Typography color="error" sx={{ mb: 2 }}>
                {errors.form}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                height: 56,
                borderRadius: 1,
                bgcolor: "#2563eb",
                fontWeight: 600,
                fontSize: "1rem",
                mb: 2,
                '&:hover': { bgcolor: "#1d4ed8" },
              }}
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>
            <Typography align="center" variant="body2" sx={{ color: "#7A8699" }}>
              Don&apos;t have an account?{' '}
              <Link to="/signup" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>
                Sign Up
              </Link>
            </Typography>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignIn;
